import { useAtuacao } from '@/lib/delegacao/atuacao';
import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import { queryPersistMeta } from '../lib/pwa/queryPersistence';
import { useAuth } from './useAuth';

function isAxios404(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const e = err as { isAxiosError?: boolean; response?: { status?: number } };
  return e.isAxiosError === true && e.response?.status === 404;
}

export interface SubscriptionData {
  id: string | null;
  user_id: string | null;
  plano_id: string | null;
  plano: {
    id: string;
    codigo: string;
    nome: string;
    descricao: string | null;
    preco_centavos: number;
    intervalo: string;
    grupo: string;
    ativo: boolean;
  } | null;
  status: string;
  trial_inicio: string | null;
  trial_fim: string | null;
  periodo_inicio: string | null;
  periodo_fim: string | null;
  gateway_subscription_id: string | null;
  cortesia: {
    plano_grupo: string;
    motivo: string | null;
  } | null;
  /**
   * A vaga paga pelo escritório (docs/PLANO_COMPARTILHAMENTO_ESCRITORIO.md §29): o membro que não
   * paga por si usa o app inteiro pelo plano e pelos créditos de IA do Proprietário.
   */
  escritorio_que_paga?: { id: string; nome: string } | null;
  /** Limites efetivos já resolvidos pelo backend (cortesia > assinatura > vaga do escritório > trial). */
  limites?: Record<string, unknown>;
  /** Add-ons ativos (hoje só o Secretário), por cortesia ou contratados. */
  /** O pessoal (``escritorio_id`` nulo) e os dos escritórios que esta conta paga. */
  addons?: { codigo: string; origem: string; expira_em: string | null; escritorio_id?: string | null }[];
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Uma feature do plano está liberada para este usuário?
 *
 * ESPELHA o gate do backend, que é `limits.get(feature, False)` — ausente é
 * NEGADO. Tratar ausente como liberado invertia a regra e abria caminhos
 * determinísticos de 403: plano gravado antes da feature existir, `limites`
 * vazio (sem assinatura, ou plano nulo), e admin/suporte sem assinatura — que
 * caem no fallback de trial, onde a feature é falsa.
 *
 * Durante o carregamento também NÃO libera. É o oposto do `SubscriptionGate`,
 * e de propósito: lá o gate decide se o app inteiro abre (segurar seria pior
 * que liberar); aqui decide se uma entrada de feature paga aparece, e mostrar
 * um botão que responde 403 é pior do que ele surgir 200ms depois.
 */
export function useFeatureLiberada(feature: string): boolean {
  const { data } = useSubscription();
  const atuacao = useAtuacao();
  // Na conta do titular o plano é o DELE (o backend decide). O Chat de IA depende
  // ainda do interruptor da delegação; o resto aparece, e o plano do titular responde.
  if (atuacao) return feature === 'peticao_ia' ? atuacao.permiteIa : true;
  return data?.limites?.[feature] === true;
}

export function useSubscription() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<SubscriptionData | null>({
    queryKey: ['subscriptions', 'me', userId],
    enabled: !!userId,
    meta: queryPersistMeta.nonEmpty,
    queryFn: async () => {
      try {
        const { data } = await api.get<SubscriptionData>('/subscriptions/me');
        return data;
      } catch (err) {
        // 404 = sem subscription e sem cortesia → retorna null (não é erro)
        if (isAxios404(err)) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    retry: (failureCount, error) => {
      if (isAxios404(error)) return false;
      return failureCount < 1;
    },
  });
}
